import axiosGdt, {
  axiosGdtWithoutCredentials,
  setCurrentCsrfToken,
} from '../utils/axiosGdt';
import { URLs } from '../utils/urls';

import { teamLastEdition } from './buildTeam11';

import { Auth0ConfigResponse, GDTApiErrors, UserInitialData } from '@/types';
import { ResponseObtenerEquipoAnterior } from '@/types/team';
import { TEXTS, axiosErrorHandler, getPlatformId } from '@/utils';

export const fetchAppVersionNumber = axiosErrorHandler(async () => {
  const response = await axiosGdtWithoutCredentials.get(URLs.VERSION_APP);
  return response.data;
}, TEXTS.defaultError);

export const fetchAuth0Config = axiosErrorHandler(
  async (site: 'responsive' | 'webmobile' = 'responsive') => {
    const response = await axiosGdt.get<Auth0ConfigResponse | GDTApiErrors>(
      `${URLs.CONFIG_AUTH0}&sitio=${encodeURIComponent(site as string)}`
    );
    return response.data;
  },
  TEXTS.auth0Error
);

type initialDataParams = {
  idToken: string;
};

export const getAccountInitialData = axiosErrorHandler(
  async ({ idToken }: initialDataParams) => {
    const config = {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    };

    const form = new URLSearchParams();
    form.append('id_token', idToken);

    const response = await axiosGdt.post<UserInitialData | GDTApiErrors>(
      `${URLs.LOGIN_AUTH0}&sitio=responsive&idPlataforma=${getPlatformId()}`,
      form,
      config
    );

    if (response.data.estado) {
      setCurrentCsrfToken(response.data.csrfToken, response.data.idUsuarioDT);
      return await augmentWithPrevTeam(response.data);
    } else {
      return response.data;
    }
  },
  TEXTS.initialDataError
);

export type PublicacionStatus = {
  estado: boolean;
  uvr_factual_orden: number;
  uvr_factual_olock: string; // Example: "-8794"
  uvr_f5_idVersionRanking: number;
  uvr_idVersionRanking: number;
  uvr_ordenFecha: number;
};

/*
type PublicacionStatus = Pick<
  UserInitialData,
  | 'uvr_factual_orden'

>;
*/
export const checkNuevaPublicacion = axiosErrorHandler(async () => {
  const response = await axiosGdt.get<PublicacionStatus>(
    URLs.CHECK_NUEVA_PUBLICACION
  );
  return response.data;
}, TEXTS.defaultError);

export const refreshAccountInitialData = axiosErrorHandler(async () => {
  const response = await axiosGdt.get<UserInitialData | GDTApiErrors>(
    `${
      URLs.REFRESH_DATOS_INICIALES
    }&sitio=responsive&idPlataforma=${getPlatformId()}`
  );

  if (response.data.estado) {
    setCurrentCsrfToken(response.data.csrfToken, response.data.idUsuarioDT);
    return await augmentWithPrevTeam(response.data);
  } else {
    return response.data;
  }
}, TEXTS.initialDataError);

export const getAccountInitialDataViaOTP = axiosErrorHandler(
  async ({ hashLogin }: { hashLogin: string }) => {
    const config = {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    };

    const form = new URLSearchParams();
    form.append('hash', hashLogin);

    const response = await axiosGdt.post<UserInitialData | GDTApiErrors>(
      `${URLs.LOGIN_AUTH0}&sitio=responsive&idPlataforma=${getPlatformId()}`,
      form,
      config
    );

    if (response.data.estado) {
      setCurrentCsrfToken(response.data.csrfToken, response.data.idUsuarioDT);
      return await augmentWithPrevTeam(response.data);
    } else {
      return response.data;
    }
  },
  TEXTS.initialDataError
);

/**
 * Verifica si el usuario está logueado (cookie de GDT, no auth0)
 */
export const checkLoggedIn = axiosErrorHandler(async () => {
  const response = await axiosGdt.get<GDTApiErrors>(URLs.CHECK_LOGGED_IN);
  return response.data;
}, TEXTS.initialDataError);

export type ResponseGetAccountInitialData = UserInitialData & {
  prevTeam?: {
    estado?: boolean;
    mensaje?: string;
  } & ResponseObtenerEquipoAnterior;
} & {
  estado: boolean;
  mensaje?: string;
  codError?: number;
};

export function setShouldCheckLoggedInNative(bShouldCheck: boolean) {
  if (bShouldCheck) {
    sessionStorage.setItem('checkLoggedIn_jsessionid', 'true');
  } else {
    sessionStorage.removeItem('checkLoggedIn_jsessionid');
  }
}

export function isShouldCheckLoggedInNative() {
  return !!sessionStorage.getItem('checkLoggedIn_jsessionid');
}

/**
 * Sólo para debug.
 * Invalida la sesión en el servidor (java).
 */
export const sessionInvalidateJava = axiosErrorHandler(async () => {
  const response = await axiosGdt.post<GDTApiErrors>(
    URLs.SESSION_INVALIDATE_DBG
  );
  return response.data;
}, TEXTS.defaultError);

/**
 * Toma la respuesta inicial y le suma info del equipo anterior (sólo si no confirmó el equipo).
 */
const augmentWithPrevTeam = async (data: UserInitialData) => {
  let prevTeamResponse: ResponseObtenerEquipoAnterior | GDTApiErrors = {
    estado: true,
    existe: false,
  };
  if (!data.tiene_equipo) {
    // no tiene equipo confirmado,
    // le agrego info necesaria para pantalla 'armado de equipo'
    prevTeamResponse = await teamLastEdition();
  }
  return {
    ...data,
    ...(prevTeamResponse.estado && {
      prevTeam: {
        ...prevTeamResponse,
      },
    }),
  };
};
